import React, { useState, useEffect } from "react";
import LayoutContent from "../../LayoutContent";
import Header from "../../Header/Header";
import Breadcrumb from "../../Header/Breadcrumb";
import MtiButton from "../../Buttons/MtiButton";
import SettingsNavSubMenu from "../SettingsNavSubMenu";
import TabPanel from "../../PlateTypeEditor/TabPanel";
import { useConfirm } from "../../Hooks/WindowHooks";
import HotelOffsetContent from "./HotelCalibration/HotelOffsetContent";
import { useTypedSelector } from "../../../redux/reducers/RootReducer";
import { getModules } from "../../../redux/reducers/ModuleReducer";
import { EModuleType } from "../../../Model/EModuleType";
import { getModuleCapability } from "../../../redux/reducers/ModuleCapabilitiesReducer";
import StorageCalibrationContent from "./StorageCalibration/StorageCalibrationContent";
import PsrCameraManuanControl from "../Control/PsrCameraManualControl";
import { ECarrierAutoCalibrationType } from "../../../Model/PsrCapabilites";
import Modal from "../../Common/Modal";
import DangerModalView from "./DangerModalView";
import { ECarrier } from "../../../Model/ECarrier";
import CalibrationApi from "../../../api/CalibrationApi";
import { EArmType } from "../../../Model/Waypoint";
import WaypointUtil from "../../../Utility/WaypointUtil";
import { Ri360AutoCalibrationState } from "../../../Model/Calibration";
import AutoCalibrationView from "./StorageCalibration/AutoCalibration/AutoCalibrationView";
import LIveImagePsrCamera from "../Control/PsrCameraLiveImage";
import StorageCalibrationCommandPanel from "./StorageCalibration/StorageCalibrationCommandPanel";
import HeaderButton from "../../Header/HeaderButton";
import SwitchButton from "../../Buttons/SwitchButton";
import PsrCameraApi from "../../../api/PsrCameraApi";
import { getModuleState } from "../../../redux/reducers/ModuleStateReducer";
import PsrCameraLiveImage from "../Control/PsrCameraLiveImage";
import SlotCorrectionContent from "./SlotCorrection/SlotCorrectionContent";
import ArmMover from "./SlotCorrection/ArmMover";
import Layout from "../../Layout";
import PreferenceApi from "../../../api/PreferenceApi";

type StorageCalibrationType =
  | "STORAGE CALIBRATION"
  | "HOTEL OFFSET"
  | "SLOT CORRECTION";

const StorageCalibrationTabs: StorageCalibrationType[] = [
  "STORAGE CALIBRATION",
  "HOTEL OFFSET",
  "SLOT CORRECTION",
];

const getCachedSelectedTab = (): StorageCalibrationType | null => {
  const tab = localStorage.getItem("SETTING_STORAGE_TAB");
  if (tab == null) return null;

  return tab as StorageCalibrationType;
};

const setCacheSelectedTab = (settingsTab: StorageCalibrationType) => {
  localStorage.setItem("SETTING_STORAGE_TAB", settingsTab);
};

export default function SettingsStorageRi360() {
  const psrModules = useTypedSelector((state) =>
    getModules(state, EModuleType.PSR)
  );
  const [carriers, setCarriers] = useState([] as ECarrier[]);
  const [autoCalibrationResults, setAutoCalibrationResults] = useState(
    [] as Ri360AutoCalibrationState[]
  );
  const [isEnableAccessHotelOffset, setIsEnableAccessHotelOffset] =
    useState(false);
  const [showCannotAccessHotelOffset, setShowCannotAccessHotelOffset] =
    useState(false);
  const [showSlotCorrection, setShowSlotCorrection] = useState(false);

  const psrModule = psrModules[0];

  if (psrModule === undefined) {
    throw new Error("Psr Module is undefined");
  }

  const psrIdentifier = psrModule.identifier;
  const psrCapability = useTypedSelector((state) =>
    getModuleCapability(state, psrIdentifier)
  ).psr;

  if (psrCapability === undefined) {
    throw new Error("Psr Capability is undefined");
  }

  if (psrCapability.carrierAutoCalibration === undefined) {
    throw new Error("Psr CarrierAutoCalibration is undefined");
  }

  const carrierAutoCalibrationCapability = psrCapability.carrierAutoCalibration;
  const carrierAutoCalibrationType =
    carrierAutoCalibrationCapability.carrierAutoCalibrationType;
  const psrCameraId = carrierAutoCalibrationCapability.psrCameraId;

  const userConfirmLeaveTab = useConfirm(
    "Changes are not saved, are you sure you want to leave Settings - Storage - X"
  );
  userConfirmLeaveTab.setOnCancel(() => {
    return;
  });
  const [storageCalibrationType, setStorageCalibrationType] = useState(
    getCachedSelectedTab() ?? ("STORAGE CALIBRATION" as StorageCalibrationType)
  );
  const selectedTabIndex = StorageCalibrationTabs.findIndex(
    (h) => h === storageCalibrationType
  );

  const moduleState = useTypedSelector((state) =>
    getModuleState(state, psrIdentifier)
  );

  const psrState = moduleState.psr;

  const [isCalibrationUpdated, setIsCalibrationUpdated] = useState(false);

  const [onApply, setOnApply] = useState({
    execute: () => {},
    disabled: true,
  });
  const [onCancel, setOnCancel] = useState({
    execute: () => {},
    disabled: true,
  });

  const onClickTab = (header: string) => {
    if (
      isCalibrationUpdated &&
      storageCalibrationType !== (header as StorageCalibrationType)
    ) {
      userConfirmLeaveTab.setOnOk(() => {
        setStorageCalibrationType(header as StorageCalibrationType);
        onCancel.execute();
        setIsCalibrationUpdated(false);
      });
      userConfirmLeaveTab.showConfirmationAlert();
      return;
    } else {
      setStorageCalibrationType(header as StorageCalibrationType);
      if (header === "HOTEL OFFSET" && !isEnableAccessHotelOffset) {
        setShowCannotAccessHotelOffset(true);
        setStorageCalibrationType("STORAGE CALIBRATION");
      }
    }
  };

  useEffect(() => {
    CalibrationApi.getWaypoint().then((moduleWaypoints) => {
      const carriers = WaypointUtil.getCarriers(
        moduleWaypoints,
        psrModule.identifier,
        EArmType.PsrArm
      ).filter((c) => c != ECarrier.Imager);

      setCarriers(carriers);
    });
  }, []);

  useEffect(() => {
    CalibrationApi.GetAutoCalibrationResult().then((autoCalibrationResults) => {
      setAutoCalibrationResults(autoCalibrationResults);
    });
    userConfirmLeaveTab.setOnOk(onCancel.execute);
    userConfirmLeaveTab.setShowOnLeave(isCalibrationUpdated);
  }, [isCalibrationUpdated]);

  useEffect(() => {
    let enableAccessHotelOffset = false;
    carriers.forEach((carrier) => {
      const autoCalibrationResult = autoCalibrationResults.find(
        (result) => result.carrier === carrier
      );
      const isManualCalibrated = autoCalibrationResult
        ? autoCalibrationResult.isOverriden
        : true;
      if (isManualCalibrated) {
        enableAccessHotelOffset = true;
      }
    });
    setIsEnableAccessHotelOffset(enableAccessHotelOffset);
  }, [carriers, autoCalibrationResults]);

  useEffect(() => {
    switch (storageCalibrationType) {
      case "STORAGE CALIBRATION":
        userConfirmLeaveTab.setMessage(
          "Changes are not saved, are you sure you want to leave Settings - Storage - Storage Calibrations"
        );
        break;
      case "HOTEL OFFSET":
        userConfirmLeaveTab.setMessage(
          "Changes are not saved, are you sure you want to leave Settings - Storage - Hotel Offset"
        );
        break;
      case "SLOT CORRECTION":
        userConfirmLeaveTab.setMessage(
          "Changes are not saved, are you sure you want to leave Settings - Storage - Slot Correction"
        );
        break;
    }
    setCacheSelectedTab(storageCalibrationType);
  }, [storageCalibrationType]);

  if (psrState === undefined) {
    throw new Error("Psr state is null");
  }

  const psrCameraState = psrState.psrCameras.find(
    (x) => x.psrCameraId === psrCameraId
  );

  useEffect(() => {
    PreferenceApi.getPreferences().then((values) => {
      const preference = values.find((pf) => pf.name == "PsrPreference");
      if (preference) {
        const enableSlotCorrection = preference.values.find(
          (val) => val.preferenceKey === "PsrPreference.SlotCorrection.Enabled"
        );
        const enable = enableSlotCorrection?.preferenceValue === "True";
        setShowSlotCorrection(enable);
        if(!enable && storageCalibrationType === "SLOT CORRECTION"){
          setStorageCalibrationType("STORAGE CALIBRATION");
        }
      }
    });
  }, []);

  return (
    <LayoutContent>
      <LayoutContent.Header>
        <Header>
          <Breadcrumb
            items={[
              { title: "Storage", to: "/settings/storage", disabled: false },
            ]}
          />
          <Header.RightSide>
            {storageCalibrationType === "STORAGE CALIBRATION" &&
              carrierAutoCalibrationType ===
                ECarrierAutoCalibrationType.PsrCamera && (
                <HeaderButton
                  id={"PSR-CAM-LIVE"}
                  label={"Live Stream"}
                  content={
                    <SwitchButton
                      isOn={psrCameraState?.isStreaming ?? false}
                      withLetter={true}
                      onToggle={() => {
                        PsrCameraApi.SetStreaming(
                          psrIdentifier,
                          psrCameraId,
                          !(psrCameraState?.isStreaming ?? false)
                        );
                      }}
                    />
                  }
                  onClick={() => {
                    PsrCameraApi.SetStreaming(
                      psrIdentifier,
                      psrCameraId,
                      !(psrCameraState?.isStreaming ?? false)
                    );
                  }}
                  disabled={false}
                />
              )}
            <MtiButton
              size="large"
              variant="secondary"
              style={{
                margin: "auto 10px auto 25px",
              }}
              disabled={onCancel.disabled}
              onClick={onCancel.execute}
            >
              CANCEL
            </MtiButton>
            <MtiButton
              size="large"
              disabled={onApply.disabled}
              style={{
                margin: "auto 16px auto 10px",
              }}
              onClick={onApply.execute}
            >
              APPLY
            </MtiButton>
          </Header.RightSide>
        </Header>
      </LayoutContent.Header>
      <LayoutContent.SubMenu>
        <SettingsNavSubMenu />
      </LayoutContent.SubMenu>
      <LayoutContent.Content>
        <TabPanel
          id="storage_tab"
          collapsible={false}
          variant="alternate"
          headerHeight={48}
          headerItemWidth={221}
          style={{ height: "100%", marginLeft: 12 }}
          onClickTab={onClickTab}
          allowChange={false}
          defaultSelectedIndex={selectedTabIndex}
          activeTabStyle={{
            padding: "0px",
          }}
          headerStyle={{
            margin: "0px 0px",
          }}
        >
          {{
            "STORAGE CALIBRATION":
              carrierAutoCalibrationType ===
              ECarrierAutoCalibrationType.PsrCamera ? (
                <PsrCameraLiveImage
                  psrIdentifier={psrIdentifier}
                  psrCameraId={psrCameraId}
                  style={{
                    borderTop: "1px solid #B4B4B4",
                    borderLeft: "1px solid #B4B4B4",
                  }}
                />
              ) : (
                <AutoCalibrationView
                  isCalibrationUpdated={isCalibrationUpdated}
                  setIsCalibrationUpdated={setIsCalibrationUpdated}
                  setOnApply={setOnApply}
                  setOnCancel={setOnCancel}
                />
              ),
            "HOTEL OFFSET": (
              <HotelOffsetContent
                setOnApply={setOnApply}
                setOnCancel={setOnCancel}
                isCalibrationUpdated={isCalibrationUpdated}
                setIsCalibrationUpdated={setIsCalibrationUpdated}
              />
            ),
            ...(showSlotCorrection && {
              "SLOT CORRECTION": (
                <SlotCorrectionContent
                  setOnApply={setOnApply}
                  setOnCancel={setOnCancel}
                  isSlotCorrectionUpdated={isCalibrationUpdated}
                  setIsSlotCorrectionUpdated={setIsCalibrationUpdated}
                />
              ),
            }),
          }}
        </TabPanel>
        <Modal
          show={showCannotAccessHotelOffset}
          hideOnBackdropClick={true}
          onHide={() => setShowCannotAccessHotelOffset(false)}
          style={{
            width: 686,
            minHeight: 422,
          }}
        >
          <Modal.Body>
            <DangerModalView
              title="Hotel Offset"
              message="Unable to access Hotel Offset"
              subMessage="Please maksure your carrier is on Manual Calibration Mode"
            />
          </Modal.Body>
          <Modal.Footer
            style={{
              display: "flex",
              justifyContent: "flex-end",
              alignItems: "center",
            }}
          >
            <MtiButton
              variant="primary"
              size="large"
              style={{
                width: 157,
              }}
              onClick={() => setShowCannotAccessHotelOffset(false)}
            >
              OK
            </MtiButton>
          </Modal.Footer>
        </Modal>
      </LayoutContent.Content>
      {storageCalibrationType === "STORAGE CALIBRATION" &&
        carrierAutoCalibrationType ===
          ECarrierAutoCalibrationType.PsrCamera && (
          <LayoutContent.CommandPanel
            style={{
              paddingTop: 53,
              borderTop: "6px solid rgb(130, 130, 130)",
              WebkitBoxShadow: "none",
              MozBoxShadow: "none",
              boxShadow: "none",
              paddingRight: 16,
              width: 564,
            }}
          >
            {/* <PsrCameraManuanControl
              psrIdentifier={psrIdentifier}
              psrCameraId={psrCameraId}
              disabled={false}
              style={{
                WebkitBoxShadow: "0px 4px 4px rgba(0, 0, 0, 0.25)",
                MozBoxShadow: "0px 4px 4px rgba(0, 0, 0, 0.25)",
                boxShadow: "0px 4px 4px rgba(0, 0, 0, 0.25)",
              }}
            /> */}
            <StorageCalibrationCommandPanel
              isCalibrationUpdated={isCalibrationUpdated}
              psrCameraId={psrCameraId}
              psrIdentifier={psrIdentifier}
              setIsCalibrationUpdated={setIsCalibrationUpdated}
              setOnApply={setOnApply}
              setOnCancel={setOnCancel}
            />
          </LayoutContent.CommandPanel>
        )}
      {storageCalibrationType === "SLOT CORRECTION" && (
        <LayoutContent.CommandPanel
          style={{
            width: 600,
            boxShadow: "none",
            borderTop: "6px solid #828282",
            borderLeft: "1px solid #C2C2C2",
            overflowY: "auto",
          }}
        >
          <ArmMover />
        </LayoutContent.CommandPanel>
      )}
    </LayoutContent>
  );
}
